export const analyticsArticles = [
  {
    slug: 'audience-metrics',
    title: 'Audience metrics',
    summary: 'Learn how Audience detections become impressions, views, engagement, and aggregate demographic insights in the Dashboard.',
    category: 'analytics',
    sources: [
      { file: 'accuracy-and-metrics.pdf' },
      { file: 'audience-analytics---dashboard-charts.pdf' },
      { file: 'audience-cms-analytics---dashboard-charts.pdf' },
      { file: 'how-does-the-technology-work.pdf' },
      { file: 'privacy.pdf' }
    ],
    sections: [
      {
        id: 'core-metrics',
        title: 'Understand impressions and views',
        paragraphs: [
          'Audience analytics begins when a camera-based Device detects a person in its field of view. Before those events contribute to the reported metrics, the Dashboard applies the maximum attention and session limits saved in the Site Group preferences. This keeps unusually long events from distorting the result.',
          'An eligible detection counts as an impression. It becomes a view when its attention time is greater than the Site Group view threshold, which the Dashboard initially sets to 0.5 seconds. Because an authorized User can edit that threshold, confirm the current preference before comparing Site Groups or explaining a change in view rate.'
        ],
        paragraphLinks: [{
          label: 'Site Group preferences',
          slug: 'profile-and-site-groups',
          section: 'site-group-preferences'
        }],
        tables: [
          {
            caption: 'Core Audience metrics',
            headers: ['Metric', 'Meaning'],
            rows: [
              ['Impressions', 'Eligible Audience detections in the selected scope and period.'],
              ['Views', 'Eligible impressions whose attention time is greater than the Site Group view threshold.'],
              ['Gaze Thru Rate', 'Views divided by impressions, expressed as a percentage. Also called Engagement Rate.'],
              ['Attention Time', 'Time, in seconds, that a detected person looks toward the camera. Older exports may call it dwell time.'],
              ['Session Time', 'Time, in seconds, that a detected person remains in the camera frame.']
            ]
          }
        ],
        steps: [
          'Choose Audience in Charts and confirm the source selection and period.',
          'Read Impressions, Confirmed Views, Engagement Rate, and average attention time together.',
          'Use the information control beside a metric to review its definition.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-audience-kpis-and-demographics.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Audience KPIs and demographic widgets',
            caption: 'Impressions and Confirmed Views describe different parts of the measured audience.'
          }
        ]
      },
      {
        id: 'demographics',
        title: 'Interpret demographic classifications',
        paragraphs: [
          'Audience demographic charts summarize supported classifications from eligible views. Read them as aggregate patterns across the selected scope and period, not as identity records or claims about a particular person.'
        ],
        tables: [
          {
            caption: 'Age groups used by the current Dashboard',
            headers: ['Group', 'Age'],
            rows: [
              ['Young', '0 to 19'],
              ['Young Adult', '20 to 29'],
              ['Adult', '30 to 59'],
              ['Senior', '60 or older']
            ]
          },
          {
            caption: 'Emotion groups used by the current Dashboard',
            headers: ['Group', 'Source classifications'],
            rows: [
              ['Positive', 'Happy and Surprised'],
              ['Neutral', 'Neutral, Sad, and Confused'],
              ['Negative', 'Fear, Angry, and Disgust']
            ]
          }
        ],
        bullets: [
          'Gender charts group supported gender classifications for the selected period.',
          'Age values are grouped into the Dashboard bands before charting.',
          'Emotion charts may expose either source classifications or the grouped Positive, Neutral, and Negative categories, depending on the widget.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-audience-attention-and-demographic-breakdowns.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Audience charts with attention time and demographic breakdowns',
            caption: 'Review duration and demographic breakdowns within the same scope.'
          }
        ]
      },
      {
        id: 'audience-charts',
        title: 'Read Audience charts',
        paragraphs: [
          'Audience widgets let you move from overall impressions and views into demographic and time-based patterns. A total, an average duration, and a percentage answer different questions, even when they appear beside one another. Use the chart information control to confirm the aggregation before drawing a comparison.'
        ],
        tables: [
          {
            caption: 'Audience chart groups',
            headers: ['Chart group', 'What it shows'],
            rows: [
              ['Views, Impressions, and Gaze Thru Rate', 'Overall totals and the share of impressions that became views.'],
              ['Views vs Impressions', 'Both totals over the selected time interval.'],
              ['Gender and Emotion', 'Distribution of classifications derived from views.'],
              ['Attention and Session Time', 'Average duration in seconds for the selected interval.'],
              ['Views by Emotion, Gender, or Age Group', 'View totals split by the selected demographic dimension.'],
              ['Attention Time by Gender', 'Average attention time for each supported gender classification.'],
              ['Impressions and Views by Hour of Day', 'Totals regrouped by hour of day.'],
              ['Totals and Average by Day of Week', 'Total and daily average impressions for each weekday.'],
              ['Average Time by Hour or Weekday', 'Average attention and session time by hour of day or day of week.']
            ]
          }
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-audience-emotions-and-zones.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Audience emotion charts and zone breakdowns',
            caption: 'Scroll through the widgets to compare classifications and configured zones.'
          }
        ]
      },
      {
        id: 'heatmaps',
        title: 'Use heatmaps',
        paragraphs: [
          'When heatmaps are enabled for a Device, the widget combines session volume and time spent to show where activity occurred within the camera view. Colors near the high end of the scale mean more activity, more time spent, or a combination of both. Interpret the image as a relative pattern; it does not provide a count at each point.',
          'Select one Device before opening demographic filters. You can then filter by supported gender, age, and emotion values, and combine those filters when the selected data supports the combination.'
        ],
        figures: [
          {
            src: 'assets/documentation/audience-heatmap-example-redacted.png',
            alt: 'Audience heatmap over a street scene with a color scale from low to high activity',
            caption: 'A heatmap combines detection volume and time spent to show activity across the camera view.'
          }
        ]
      },
      {
        id: 'audience-reports',
        title: 'Read generated Audience reports',
        paragraphs: [
          'When you configure a report, the Dashboard loads the CSV fields available for the selected solution. Older Audience exports may use the field names below, while the current dialog reflects the fields available to your deployment. Exports and Reports explains how to choose the range, grouping, schedule, and recipient.'
        ],
        paragraphLinks: [{
          label: 'Exports and Reports',
          slug: 'exports-and-reports',
          section: 'custom-csv'
        }],
        tables: [
          {
            caption: 'Audience report fields',
            headers: ['Field', 'Meaning'],
            rows: [
              ['timestamp', 'Reporting bucket determined by the selected range and interval.'],
              ['deviceName and deviceId', 'Device labels used to associate a row with its source Device.'],
              ['emotion and gender', 'Supported demographic classifications for the row.'],
              ['age', 'Average detected age for the row.'],
              ['isImpression and isView', 'Impression and view totals.'],
              ['dwellTime or AttentionTime', 'Average attention time; dwell time is the legacy field name.'],
              ['SessionTime', 'Average session time.']
            ]
          },
          {
            caption: 'Audience report layouts',
            headers: ['Layout', 'Row structure'],
            rows: [
              ['Default', 'Breaks supported demographic classifications into separate rows for each reporting bucket.'],
              ['By Device', 'Places supported demographic totals in columns, with one Device row for each reporting bucket.']
            ]
          }
        ]
      },
      {
        id: 'audience-cms',
        title: 'Review Audience CMS analytics',
        paragraphs: [
          'Audience CMS analytics places supported Audience measurements alongside matched proof-of-play records. This can help a media team connect what played with the audience activity measured at the same installation. The dimensions you can use depend on the configured CMS integration.'
        ],
        bullets: [
          'Views by Campaign groups matched Audience data by campaign.',
          'Views by Site lists matched views and impressions by site.',
          'Views by Media lists matched views and impressions by media item.',
          'Generated CMS reports combine the selected Audience fields with available playlog fields.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-broadsign-audience-site-campaign-media-breakdowns.jpg',
            frame: { width: 2128, height: 1008, sourceWidth: 2362 },
            alt: 'Broadsign Audience tables grouped by Site, campaign, and media',
            caption: 'Matched proof-of-play data adds Site, campaign, and media context to Audience measurements.'
          }
        ]
      },
      {
        id: 'measurement-flow',
        title: 'How Audience measurement works',
        paragraphs: [
          'The Device detects a person, evaluates the supported classifications, and produces numerical events for analytics. The analytics service then applies the Site Group scope and thresholds before aggregating eligible events into metrics and chart series.',
          'Audience uses face detection to determine that a face is present. It does not match that person against a known identity, and its aggregate classifications should not be interpreted as identification.'
        ],
        figures: [
          {
            src: 'assets/documentation/audience-processing-flow.png',
            alt: 'Audience processing flow from camera detection through demographic classification and analytics data',
            caption: 'Audience analytics are derived from detection and classification events produced on the Device.'
          }
        ]
      },
      {
        id: 'preview-and-privacy',
        title: 'Handle previews and aggregate data responsibly',
        paragraphs: [
          'A preview helps an authorized operator check the camera angle or field of view; it is not an analytics event. Request one only when it serves that operational purpose, limit access to authorized Users, and follow your organization’s privacy and retention policies. Network, credentials, and previews provides additional operational guidance.',
          'Use Audience results as aggregate measurements. Do not describe demographic classifications as identification, and do not infer a person’s identity from the reported metrics.'
        ],
        paragraphLinks: [{
          label: 'Network, credentials, and previews',
          slug: 'network-security-and-previews',
          section: 'previews-and-recordings'
        }]
      }
    ],
    dashboardLinks: [
      { label: 'Open Charts', to: '/charts' },
      { label: 'Open Profile', to: '/profile' }
    ],
    related: ['analytics-and-insights', 'traffic-metrics', 'network-security-and-previews'],
    legacy: false
  },
  {
    slug: 'traffic-metrics',
    title: 'Traffic metrics',
    summary: 'Understand how Traffic deployments turn vehicle and people activity into counts, speed, zone, session, and estimated-impression metrics.',
    category: 'analytics',
    sources: [
      { file: 'vehicle-crowd---dashboard-charts.pdf' },
      { file: 'vehicle-detection---dashboard-charts.pdf' },
      { file: 'vehicle-recognition---dashboard-charts.pdf' },
      { file: 'vehicle-recognition-cms-analytics---dashboard-charts.pdf' }
    ],
    sections: [
      {
        id: 'people-and-vehicle-counts',
        title: 'Read people and vehicle charts',
        paragraphs: [
          'Traffic can bring supported people and vehicle analytics into the same view. Begin with the summary widgets to understand the selected period, then move into the time, zone, and classification charts when you need to explain where a total came from.'
        ],
        tables: [
          {
            caption: 'Traffic summary widgets',
            headers: ['Widget', 'Meaning'],
            rows: [
              ['Vehicle Total', 'Detected vehicles in the selected scope and period.'],
              ['Person Total', 'Detected people in the selected scope and period.'],
              ['Peak Count', 'Highest count in a reporting bucket.'],
              ['Average Count per Day', 'Average daily count for the selected period.'],
              ['Vehicles by Type', 'Percentage distribution of supported vehicle classifications.'],
              ['Gender', 'Percentage distribution of supported person classifications when available.']
            ]
          }
        ],
        bullets: [
          'Vehicle Count by Zone splits vehicle totals across configured zones.',
          'Vehicle Speed by Zone reports average vehicle speed for each configured zone.',
          'Vehicle and Person Totals and Average by Day of Week show totals and daily averages for each weekday.',
          'Vehicle and Person Counts by Hour of Day regroup counts by hour.',
          'Vehicle Counts by Zone splits each zone total by supported vehicle type.'
        ],
        figures: [
          {
            src: 'assets/documentation/traffic-zone-breakdown.png',
            alt: 'Traffic analytics table showing vehicle counts grouped by Device, zone, and vehicle type',
            caption: 'Zone breakdowns connect each count to a configured Device zone and detected type.'
          }
        ]
      },
      {
        id: 'vehicle-counts-and-speed',
        title: 'Read vehicle count and speed charts',
        paragraphs: [
          'Traffic charts can show how many vehicles passed through the scene and, when the installation supports it, how fast they were moving. If the Device has more than one zone, the count and speed charts show a separate series for each one. Vehicle camera positioning and zones explains how those regions relate to the physical scene.'
        ],
        paragraphLinks: [{
          label: 'Vehicle camera positioning and zones',
          slug: 'vehicle-camera-positioning',
          section: 'regions-and-zones'
        }],
        tables: [
          {
            caption: 'Traffic count and speed charts',
            headers: ['Chart', 'What it shows'],
            rows: [
              ['Count Total', 'Detected vehicles in the selected scope and period.'],
              ['Peak Count', 'Highest vehicle count in a reporting bucket.'],
              ['Average Count per Day', 'Average daily vehicle count.'],
              ['Average Speed', 'Average known vehicle speed converted to the Site Group speed unit.'],
              ['Vehicle Count or Count by Zone', 'Vehicle totals over time, with a series for each configured zone when applicable.'],
              ['Vehicle Speed or Speed by Zone', 'Average speed over time for each applicable zone.'],
              ['Totals and Average by Day of Week', 'Total and daily average counts grouped by weekday.'],
              ['Count by Hour of Day', 'Vehicle counts grouped by hour.'],
              ['Count by Zone', 'Vehicle totals grouped by configured zone.']
            ]
          }
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-traffic-speed-hourly-and-weekday-patterns.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Traffic speed, hour-of-day, and weekday charts',
            caption: 'Use the time patterns to identify when traffic is strongest.'
          }
        ]
      },
      {
        id: 'traffic-count-and-speed-reports',
        title: 'Read Traffic count and speed reports',
        paragraphs: [
          'The Dashboard loads the available CSV fields for Traffic when you configure a report. The table below describes common count and speed fields, but the current report dialog remains the source of truth for a particular Site Group. See Exports and Reports for the reporting workflow.'
        ],
        paragraphLinks: [{
          label: 'Exports and Reports',
          slug: 'exports-and-reports',
          section: 'custom-csv'
        }],
        tables: [
          {
            caption: 'Traffic count and speed report fields',
            headers: ['Field', 'Meaning'],
            rows: [
              ['timestamp', 'Reporting bucket determined by the selected range and interval.'],
              ['deviceName and deviceId', 'Device labels used to associate a row with its source Device.'],
              ['zoneId', 'Configured zone associated with the detections.'],
              ['direction', 'Recorded crossing direction when supported by the deployment.'],
              ['count', 'Number of counted detections in the row.'],
              ['estimated_impressions', 'Modeled exposure total derived from vehicle-type multipliers.'],
              ['speed', 'Average known speed in the Site Group speed unit.'],
              ['sessionTime', 'Average time, in seconds, spent in the region of interest.']
            ]
          }
        ]
      },
      {
        id: 'vehicle-classification',
        title: 'Read vehicle classification charts',
        paragraphs: [
          'Traffic can add vehicle-type breakdowns when the installed model supports classification. The labels available in a chart depend on the deployed model and Device configuration, and can include car, motorcycle, bus, bicycle, and truck.'
        ],
        tables: [
          {
            caption: 'Traffic vehicle classification charts',
            headers: ['Chart', 'What it shows'],
            rows: [
              ['Core count and speed widgets', 'Total count, peak count, average count per day, and average known speed.'],
              ['Vehicles by Type', 'Percentage distribution of supported vehicle classifications.'],
              ['Vehicle Count', 'Vehicle totals split by supported type.'],
              ['Average Session Time', 'Average time a tracked vehicle remains in the region of interest.'],
              ['Types by Zone', 'Vehicle totals split by configured zone and supported type.'],
              ['Weekday and hour charts', 'Counts, daily averages, speed, or session time regrouped by weekday or hour.']
            ]
          }
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-traffic-counts-classification-estimated-impressions.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Traffic totals, vehicle-type distribution, and estimated impressions',
            caption: 'Compare measured vehicle counts with the modeled impression estimate.'
          }
        ]
      },
      {
        id: 'speed-session-and-impressions',
        title: 'Interpret speed, session time, and estimated impressions',
        paragraphs: [
          'Average speed leaves out detections whose speed is marked unknown, then converts the result to the speed unit saved for the Site Group. Average session time describes how long tracked objects remain in view or inside the configured region of interest.',
          'Estimated impressions use the editable Site Group multiplier for each normalized vehicle type. If a detected type has no configured multiplier, the analytics query uses a factor of one. The result is a modeled exposure estimate, not a direct count of people, so share it together with the multiplier assumptions behind it. Profile and Site Groups explains where those preferences are managed.'
        ],
        paragraphLinks: [{
          label: 'Profile and Site Groups',
          slug: 'profile-and-site-groups',
          section: 'site-group-preferences'
        }],
        bullets: [
          'Authorized Users can edit vehicle multipliers in Site Group preferences.',
          'Review multiplier values for the installation instead of assuming a fixed default from a saved example or screenshot.',
          'Changing a multiplier changes estimated-impression results; it does not change the underlying vehicle count.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-traffic-session-time-and-types-by-zone.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Traffic session time and vehicle types grouped by zone',
            caption: 'Zone breakdowns connect duration and vehicle classification with parts of the scene.'
          }
        ]
      },
      {
        id: 'vehicle-classification-reports',
        title: 'Read vehicle classification reports',
        paragraphs: [
          'Traffic reports can add vehicle type when the deployment supports that classification. Use the current report dialog to confirm what is available for the selected Site Group and solution.'
        ],
        tables: [
          {
            caption: 'Traffic vehicle classification report fields',
            headers: ['Field', 'Meaning'],
            rows: [
              ['timestamp', 'Reporting bucket determined by the selected range and interval.'],
              ['deviceName and deviceId', 'Device labels used to associate a row with its source Device.'],
              ['vehicleType', 'Supported vehicle classification; present for recognition data.'],
              ['zoneId', 'Configured zone associated with the detections.'],
              ['direction', 'Recorded crossing direction when supported by the deployment.'],
              ['count', 'Number of counted detections in the row.'],
              ['estimated_impressions', 'Modeled exposure total derived from vehicle-type multipliers.'],
              ['speed', 'Average known speed in the Site Group speed unit.'],
              ['sessionTime', 'Average time, in seconds, spent in the region of interest.']
            ]
          }
        ]
      },
      {
        id: 'traffic-cms-analytics',
        title: 'Review Traffic CMS analytics',
        paragraphs: [
          'With a configured Broadsign integration, Traffic analytics can group matched proof-of-play results by Site, campaign, or media item and show the associated detection zone. This lets a media team examine playback context and measured vehicle activity in the same analysis.'
        ],
        paragraphLinks: [{ label: 'Broadsign integration', slug: 'broadsign-integration', section: 'overview' }],
        bullets: [
          'Detection by Site groups matched counts by site and zone.',
          'Detection by Campaign groups matched counts by campaign and zone.',
          'Detection by Media groups matched counts by media item, campaign, and zone.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-broadsign-traffic-site-campaign-media-breakdowns.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Broadsign Traffic tables by Site, campaign, media, and zone',
            caption: 'Review the playback dimensions alongside the measured Traffic results.'
          }
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Charts', to: '/charts' },
      { label: 'Open Profile', to: '/profile' }
    ],
    related: ['analytics-and-insights', 'audience-metrics', 'vehicle-camera-positioning'],
    legacy: false
  }
];
